import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { NavMenuItem } from '../../menu';
import { IconPlus } from '../Icons';

interface SidebarProps {
  headerHeight: number;
  siderWidth: number;
  isMobile: boolean;
  menuGroups: { primary: NavMenuItem[]; secondary: NavMenuItem[] };
  activeKey: string;
  onMenuClick: (item: NavMenuItem) => void;
  onCreate: () => void;
}

export function Sidebar({
  headerHeight,
  siderWidth,
  isMobile,
  menuGroups,
  activeKey,
  onMenuClick,
  onCreate,
}: SidebarProps) {
  const { t } = useTranslation('app');

  return (
    <aside
      className="fixed left-0 z-30 border-r border-[var(--cp-border)] bg-[var(--cp-nav-bg)] text-[var(--cp-fg)]"
      style={{ top: headerHeight, width: siderWidth, height: `calc(100vh - ${headerHeight}px)` }}
    >
      <div className="flex h-full flex-col gap-3 p-3">
        <div className="space-y-2">
          <button
            type="button"
            onClick={onCreate}
            className={classNames(
              'w-full rounded-lg bg-[var(--cp-primary-bg)] text-[var(--cp-primary-fg)] hover:bg-[var(--cp-primary-bg-hover)]',
              isMobile ? 'flex h-10 items-center justify-center' : 'flex items-center gap-2 px-3 py-2 text-sm font-semibold',
            )}
          >
            <IconPlus />
            {!isMobile && <span>{t('layout.sider.create')}</span>}
          </button>
        </div>

        <nav className="flex-1 space-y-1">
          {menuGroups.primary.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onMenuClick(item)}
              className={classNames(
                'group flex w-full items-center rounded-lg text-left text-sm font-medium',
                isMobile ? 'h-10 justify-center' : 'gap-2 px-3 py-2',
                activeKey === item.key
                  ? 'bg-[var(--cp-secondary-bg)] text-[var(--cp-fg)]'
                  : 'text-[var(--cp-muted)] hover:bg-[var(--cp-secondary-bg-hover)] hover:text-[var(--cp-fg)]',
              )}
            >
              <span
                className={classNames(
                  activeKey === item.key ? 'text-[var(--cp-fg)]' : 'text-[var(--cp-muted-2)] group-hover:text-[var(--cp-fg)]',
                )}
              >
                {item.icon}
              </span>
              {!isMobile && <span>{t(item.name)}</span>}
            </button>
          ))}
        </nav>

        <div className="border-t border-[var(--cp-border)] pt-3">
          {menuGroups.secondary.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onMenuClick(item)}
              className={classNames(
                'group flex w-full items-center rounded-lg text-left text-sm font-medium text-[var(--cp-muted)] hover:bg-[var(--cp-secondary-bg-hover)] hover:text-[var(--cp-fg)]',
                isMobile ? 'h-10 justify-center' : 'gap-2 px-3 py-2',
              )}
            >
              <span className="text-[var(--cp-muted-2)] group-hover:text-[var(--cp-fg)]">{item.icon}</span>
              {!isMobile && <span>{t(item.name)}</span>}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
